import { io, Socket } from 'socket.io-client';
import { config as appConfig } from '@/config';
import { getCurrentAccessToken } from './auth-session';
import {
  ensureOptionalAgentServiceAvailable,
  recordAgentServiceFailure,
  recordAgentServiceSuccess,
  shouldSkipOptionalAgentServiceRequest,
  subscribeAgentServiceAvailability,
} from './agentServiceAvailability';

let socket: Socket | null = null;
let socketToken: string | null = null;
let socketConnectProbe: Promise<void> | null = null;

function getToken(): string | null {
  return getCurrentAccessToken();
}

function shouldSuppressConnectErrorLog(message: string): boolean {
  return (
    import.meta.env.MODE === 'test' &&
    (message === 'websocket error' || message === 'xhr poll error')
  );
}

function connectSocketWhenServiceIsAvailable(targetSocket: Socket): void {
  if (targetSocket.connected || socketConnectProbe) {
    return;
  }

  socketConnectProbe = (async () => {
    const serviceAvailable = await ensureOptionalAgentServiceAvailable({ probe: true });
    if (serviceAvailable && socket === targetSocket && !targetSocket.connected) {
      targetSocket.connect();
    }
  })().finally(() => {
    socketConnectProbe = null;
  });
}

subscribeAgentServiceAvailability(() => {
  if (socket && !socket.connected && !shouldSkipOptionalAgentServiceRequest()) {
    connectSocketWhenServiceIsAvailable(socket);
  }
});

export function getAgentSocket(options: { connect?: boolean } = {}): Socket {
  const token = getToken();

  // Force reconnect if the auth token changed (different user logged in)
  if (socket && socketToken !== token) {
    socket.disconnect();
    socket = null;
    socketConnectProbe = null;
  }

  if (socket) {
    if (options.connect === true) {
      connectSocketWhenServiceIsAvailable(socket);
    }
    return socket;
  }

  // When behind a reverse proxy (e.g. /agent-api → agent service), Socket.IO
  // treats the URL path as a namespace. We need to connect to the origin with
  // namespace "/" and prepend the proxy path to the socket path instead.
  // Resolve against the current page origin so relative URLs ("/agent-api")
  // and absolute URLs both work.
  const parsedUrl = new URL(appConfig.api.agentServiceUrl, window.location.origin);
  const basePath = parsedUrl.pathname === '/' ? '' : parsedUrl.pathname.replace(/\/$/, '');

  socket = io(parsedUrl.origin, {
    path: basePath + '/user-agent-socket',
    auth: { token },
    transports: ['websocket', 'polling'],
    autoConnect: false,
    reconnection: true,
    reconnectionAttempts: 10,
    reconnectionDelay: 2000,
    timeout: 10000,
  });
  socketToken = token;

  socket.on('connect', () => {
    recordAgentServiceSuccess();
  });

  socket.on('connect_error', (err) => {
    recordAgentServiceFailure(err.message);
    socket?.disconnect();
    if (shouldSuppressConnectErrorLog(err.message)) {
      return;
    }
    console.warn('[AgentSocket] Connection error:', err.message);
  });

  if (options.connect === true) {
    connectSocketWhenServiceIsAvailable(socket);
  }

  return socket;
}

export function disconnectAgentSocket(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
  socketConnectProbe = null;
  socketToken = null;
}

export function isAgentSocketConnected(): boolean {
  return socket?.connected ?? false;
}

/**
 * Force-reconnect the agent socket with a fresh auth token.
 * Call this after re-authentication to replace the stale socket.
 */
export function reconnectAgentSocket(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
  }

  // Re-attach chat store listeners to the new socket
  // Lazy import to avoid circular dependency
  import('@/stores/agents/chat-store').then(({ rebindAgentSocketListeners }) => {
    rebindAgentSocketListeners();
  }).catch(() => {});
}
